import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Drawer, Chip, TextField, InputAdornment, Select, MenuItem,
  FormControl, InputLabel, Pagination, CircularProgress, Tooltip, Divider,
  Collapse, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, Snackbar, Alert, Breadcrumbs, Link, Popover,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import API from '../api';

const LEVEL_COLORS = { 1: '#4fc3f7', 2: '#81c784', 3: '#ffb74d', 4: '#f06292', 5: '#ce93d8' };

function fmtEur(v) { return '€' + (parseFloat(v) || 0).toFixed(2); }
function fmtCountdown(iso, nowMs) {
  if (!iso) return '';
  const target = new Date(iso).getTime();
  let diff = Math.max(0, Math.floor((target - nowMs) / 1000));
  const d = Math.floor(diff / 86400); diff -= d * 86400;
  const h = Math.floor(diff / 3600); diff -= h * 3600;
  const m = Math.floor(diff / 60);
  const s = diff - m * 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (d > 0) return d + 'd ' + pad(h) + ':' + pad(m) + ':' + pad(s);
  return pad(h) + ':' + pad(m) + ':' + pad(s);
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' });
}
function daysSince(d) {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d)) / 86400000);
}

// ─── Tree Node (lazy-loads children on expand) ────────────────────────────────
function TreeNode({ node, depth, onDrillDown, payoutMap, levelSettings, viewedUserId }) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState(null); // null = not yet loaded
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [detailAnchor, setDetailAnchor] = useState(null);
  const [history, setHistory] = useState(null);       // null=not loaded, []=empty
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const hasChildren = Number(node.sub_count) > 0;
  const payments = node.payments || [];
  const latestPayment = payments.length > 0 ? payments[payments.length - 1] : null;
  const days = daysSince(node.last_payment_at);
  const trial = node.referred_status === 'trial' && payments.length === 0;
  const inactive = !trial && (days === null || days > 30);
  const levelNum = Math.min(node.level, 5);
  const levelColor = LEVEL_COLORS[levelNum] || '#888';
  const indentPx = depth * 20;

  // payout_log = source of truth. For depth>0 (lazy-loaded sub-nodes):
  // without log entry, show nothing — the sub-node data belongs to a different user.
  // For depth=0 (direct referrals): fall back to referrals.total_earned_eur if log missing.
  const logEntry = payoutMap && payoutMap[String(node.referred_id)];
  const _totalPmt = logEntry ? Number(logEntry.total_payment_amount || 0) : 0;
  const pct = _totalPmt > 0
    ? Math.round(Number(logEntry.total_base) / _totalPmt * 10000) / 100
    : (levelSettings && levelSettings[node.level]) ?? Number(node.percent_at_creation ?? 0);
  const mult = logEntry && Number(logEntry.total_base || 0) > 0
    ? Math.round(Number(logEntry.total_amount) / Number(logEntry.total_base) * 100) / 100
    : Number(node.bonus_multiplier || 1);
  const earnedAmount = logEntry ? Number(logEntry.total_amount) : (depth === 0 ? Number(node.earned_from_referral) : 0);
  const earnedBase   = logEntry ? Number(logEntry.total_base)   : (depth === 0 ? Number(node.base_amount_eur) : 0);
  const earnedBonus  = logEntry ? Number(logEntry.total_bonus)  : (depth === 0 ? Number(node.bonus_amount_eur) : 0);
  const fromLog      = Boolean(logEntry);
  const showEarned   = logEntry || depth === 0;
  const contestTitles       = (logEntry?.contest_titles       || []).filter(Boolean);
  const contestDescriptions = (logEntry?.contest_descriptions || []).filter(Boolean);
  const contestRules        = (logEntry?.contest_rules        || []).filter(Boolean);

  const toggle = async () => {
    if (!expanded && children === null && hasChildren) {
      setLoadingChildren(true);
      try {
        const r = await API.get(`/referral-earnings/${node.referred_id}`);
        const refs = r.data.referrals || [];
        // direct children = minimum level in response (global level of direct referrals)
        const minLvl = refs.length > 0 ? Math.min(...refs.map(c => c.level)) : 1;
        setChildren(refs.filter(c => c.level === minLvl));
      } catch {
        setChildren([]);
      }
      setLoadingChildren(false);
    }
    setExpanded(e => !e);
  };

  const openDetail = (e) => { e.stopPropagation(); setDetailAnchor(e.currentTarget); };
  const closeDetail = () => setDetailAnchor(null);

  const toggleHistory = async () => {
    if (history === null && !loadingHistory) {
      setLoadingHistory(true);
      try {
        const r = await API.get(`/referral-earnings/${viewedUserId}/payout-log/${node.referred_id}`);
        setHistory(r.data || []);
      } catch {
        setHistory([]);
      }
      setLoadingHistory(false);
    }
    setShowHistory(h => !h);
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        py: 0.6, pl: `${indentPx + 6}px`, pr: 1,
        borderBottom: '1px solid #111',
        bgcolor: depth === 0 ? '#0f1729' : 'transparent',
        '&:hover': { bgcolor: '#1a1a2e' },
        minHeight: 30,
      }}>
        {/* Expand toggle */}
        <Box sx={{ width: 22, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
          {hasChildren ? (
            loadingChildren
              ? <CircularProgress size={12} sx={{ color: '#555' }} />
              : (
                <IconButton size="small" onClick={toggle}
                  sx={{ color: '#555', p: 0, width: 18, height: 18 }}>
                  {expanded
                    ? <ExpandLessIcon sx={{ fontSize: 13 }} />
                    : <ExpandMoreIcon sx={{ fontSize: 13 }} />}
                </IconButton>
              )
          ) : (
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#222', mx: 'auto' }} />
          )}
        </Box>

        {/* Level badge */}
        <Typography sx={{ color: levelColor, fontSize: 10, fontWeight: 700, minWidth: 18, flexShrink: 0 }}>
          L{node.level}
        </Typography>

        {/* Username — clickable → drill down to their tree */}
        <Typography
          onClick={() => onDrillDown(node.referred_id, node.telegram_username || String(node.telegram_id))}
          sx={{
            color: '#90caf9', fontSize: 12, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            '&:hover': { textDecoration: 'underline' },
          }}>
          @{node.telegram_username || node.telegram_id}
        </Typography>

        {/* Status chip */}
        <Chip
          label={trial ? 'Trial' : inactive ? `${days ?? '∞'}д` : 'Акт'}
          size="small"
          sx={{
            bgcolor: trial ? '#37474f' : inactive ? '#bf360c' : '#1b5e20',
            color: '#fff', fontSize: 9, height: 15, flexShrink: 0,
          }}
        />

        {/* Percent + contest multiplier — click to open detail */}
        {pct > 0 && (
          <Typography
            onClick={openDetail}
            sx={{
              color: mult > 1 ? '#ffd54f' : '#aaa', fontSize: 10, flexShrink: 0,
              cursor: 'pointer', textDecoration: 'underline dotted',
              '&:hover': { color: mult > 1 ? '#ffe082' : '#ccc' },
            }}>
            {mult > 1 ? `${pct}% ×${mult}` : `${pct}%`}
          </Typography>
        )}

        {/* Referral since */}
        <Typography sx={{ color: '#555', fontSize: 10, flexShrink: 0 }}>
          реф.{fmtDate(node.referral_since)}
        </Typography>

        {/* Latest payment */}
        {latestPayment ? (
          <>
            <Typography sx={{
              color: '#4fc3f7', fontSize: 10, flexShrink: 1,
              maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {latestPayment.package_name}
            </Typography>
            <Typography sx={{ color: '#81c784', fontSize: 10, fontWeight: 600, flexShrink: 0 }}>
              {fmtEur(latestPayment.amount)}
            </Typography>
            <Typography sx={{ color: '#666', fontSize: 10, flexShrink: 0 }}>
              {fmtDate(latestPayment.confirmed_at)}
            </Typography>
          </>
        ) : (
          <Typography sx={{ color: '#333', fontSize: 10, flexShrink: 0 }}>—</Typography>
        )}

        {/* Earned — click to open full breakdown */}
        {showEarned && earnedAmount > 0 ? (
          <Typography
            onClick={openDetail}
            sx={{
              color: fromLog ? '#ffb74d' : '#a07040', fontSize: 11, fontWeight: 700,
              ml: 'auto', flexShrink: 0, cursor: 'pointer',
              textDecoration: 'underline dotted',
              '&:hover': { color: fromLog ? '#ffe082' : '#c09060' },
            }}>
            {fmtEur(earnedAmount)}
            {earnedBonus > 0 && (
              <Typography component="span" sx={{ color: '#ffd54f', fontSize: 9, ml: 0.3 }}>🏆</Typography>
            )}
            {!fromLog && depth === 0 && (
              <Typography component="span" sx={{ color: '#666', fontSize: 9, ml: 0.3 }}>~</Typography>
            )}
          </Typography>
        ) : depth > 0 && !logEntry ? (
          <Typography sx={{ color: '#444', fontSize: 10, ml: 'auto', flexShrink: 0 }}>—</Typography>
        ) : null}
      </Box>

      {/* Detail popover */}
      <Popover
        open={Boolean(detailAnchor)}
        anchorEl={detailAnchor}
        onClose={closeDetail}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { bgcolor: '#1a1a2e', border: '1px solid #2d2d4e', p: 2, maxWidth: 340, minWidth: 240 } }}
      >
        <Typography sx={{ color: '#90caf9', fontSize: 13, fontWeight: 700, mb: 1.5 }}>
          📊 @{node.telegram_username || node.telegram_id} · L{node.level}
        </Typography>

        {/* Calculation formula — always shown when logEntry exists */}
        {fromLog ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: '#888', fontSize: 10, mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Формула нарахування
            </Typography>

            {/* Step 1: base payout */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
              {_totalPmt > 0 ? (
                <>
                  <Typography sx={{ color: '#81c784', fontSize: 12, fontWeight: 600 }}>€{_totalPmt.toFixed(2)}</Typography>
                  <Typography sx={{ color: '#555', fontSize: 11 }}>×</Typography>
                  <Typography sx={{ color: '#4fc3f7', fontSize: 12, fontWeight: 600 }}>{pct}%</Typography>
                  <Typography sx={{ color: '#555', fontSize: 11 }}>=</Typography>
                  <Typography sx={{ color: '#ccc', fontSize: 12, fontWeight: 600 }}>€{earnedBase.toFixed(4)}</Typography>
                  <Typography sx={{ color: '#555', fontSize: 10 }}>(базова виплата)</Typography>
                </>
              ) : (
                <>
                  <Typography sx={{ color: '#4fc3f7', fontSize: 12, fontWeight: 600 }}>L{node.level}: {pct}%</Typography>
                  <Typography sx={{ color: '#555', fontSize: 10 }}>→ базово нараховано</Typography>
                  <Typography sx={{ color: '#ccc', fontSize: 12, fontWeight: 600 }}>{fmtEur(earnedBase)}</Typography>
                </>
              )}
            </Box>

            {/* Step 2: bonus multiplier */}
            {earnedBonus > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pl: 1, borderLeft: '2px solid #4a3800' }}>
                <Typography sx={{ color: '#ccc', fontSize: 12 }}>€{earnedBase.toFixed(4)}</Typography>
                <Typography sx={{ color: '#555', fontSize: 11 }}>×</Typography>
                <Typography sx={{ color: '#ffd54f', fontSize: 12, fontWeight: 700 }}>{mult}</Typography>
                <Typography sx={{ color: '#555', fontSize: 11 }}>=</Typography>
                <Typography sx={{ color: '#ffb74d', fontSize: 12, fontWeight: 700 }}>€{earnedAmount.toFixed(4)}</Typography>
                <Typography sx={{ color: '#ffd54f', fontSize: 10 }}>(+€{earnedBonus.toFixed(2)} бонус)</Typography>
              </Box>
            )}

            {logEntry?.payment_count > 1 && (
              <Typography sx={{ color: '#555', fontSize: 10, mt: 0.5 }}>
                Сума за {logEntry.payment_count} платежів
              </Typography>
            )}
          </Box>
        ) : depth === 0 && earnedAmount > 0 ? (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ color: '#888', fontSize: 10, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Приблизно (з даних рефералів)
            </Typography>
            <Typography sx={{ color: '#ccc', fontSize: 12 }}>
              L{node.level}: {pct}%{mult > 1 ? ` × ${mult}` : ''}
            </Typography>
          </Box>
        ) : null}

        {/* Summary line */}
        <Box sx={{ display: 'flex', gap: 2, py: 1, px: 1.5, bgcolor: '#12121f', borderRadius: 1, mb: 1.5 }}>
          <Box>
            <Typography sx={{ color: '#888', fontSize: 9 }}>Базова виплата</Typography>
            <Typography sx={{ color: '#ccc', fontSize: 12, fontWeight: 600 }}>{fmtEur(earnedBase || earnedAmount)}</Typography>
          </Box>
          {earnedBonus > 0 && (
            <Box>
              <Typography sx={{ color: '#888', fontSize: 9 }}>Бонус 🏆 ×{mult}</Typography>
              <Typography sx={{ color: '#ffd54f', fontSize: 12, fontWeight: 600 }}>+{fmtEur(earnedBonus)}</Typography>
            </Box>
          )}
          <Box sx={{ ml: 'auto' }}>
            <Typography sx={{ color: '#888', fontSize: 9 }}>Разом</Typography>
            <Typography sx={{ color: '#ffb74d', fontSize: 13, fontWeight: 700 }}>{fmtEur(earnedAmount)}</Typography>
          </Box>
        </Box>

        {/* Contest details (or fallback if bonus with no linked contest) */}
        {contestTitles.length > 0 ? contestTitles.map((title, i) => (
          <Box key={i} sx={{ p: 1.5, bgcolor: '#1e1a00', border: '1px solid #4a3800', borderRadius: 1, mb: 0.5 }}>
            <Typography sx={{ color: '#ffd54f', fontSize: 12, fontWeight: 700, mb: 0.5 }}>
              🏆 {title}
            </Typography>
            {contestDescriptions[i] && (
              <Typography sx={{ color: '#ccc', fontSize: 11, mb: 0.5, lineHeight: 1.5 }}>
                {contestDescriptions[i]}
              </Typography>
            )}
            {contestRules[i] && (
              <Box sx={{ mt: 0.5, pt: 0.5, borderTop: '1px solid #4a3800' }}>
                <Typography sx={{ color: '#888', fontSize: 9, mb: 0.25, textTransform: 'uppercase', letterSpacing: 0.5 }}>Умови</Typography>
                <Typography sx={{ color: '#bbb', fontSize: 11, lineHeight: 1.5 }}>{contestRules[i]}</Typography>
              </Box>
            )}
          </Box>
        )) : earnedBonus > 0 && (
          <Box sx={{ p: 1, bgcolor: '#1e1a00', border: '1px solid #4a3800', borderRadius: 1 }}>
            <Typography sx={{ color: '#ffd54f', fontSize: 11 }}>
              🏆 Бонусний множник ×{mult} — акцію не прив'язано до запису
            </Typography>
          </Box>
        )}

        {/* Per-payment history */}
        {fromLog && viewedUserId && (
          <Box sx={{ mt: 1.5, pt: 1, borderTop: '1px solid #1a1a3e' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button size="small" onClick={toggleHistory}
                sx={{ color: '#4fc3f7', fontSize: 10, p: 0, textTransform: 'none', minWidth: 0 }}>
                📜 Нарахування{history ? ` (${history.length})` : ''} {showHistory ? '▲' : '▼'}
              </Button>
              {loadingHistory && <CircularProgress size={10} sx={{ color: '#4fc3f7' }} />}
            </Box>

            {showHistory && history && (
              <Box sx={{ mt: 1 }}>
                {history.length === 0 ? (
                  <Typography sx={{ color: '#555', fontSize: 11 }}>Нарахувань не знайдено</Typography>
                ) : history.map((h, i) => {
                  const hDate  = h.payment_date || h.created_at;
                  const hPmt   = h.payment_amount ? Number(h.payment_amount) : null;
                  const hBase  = Number(h.base_amount);
                  const hBonus = Number(h.bonus_amount);
                  const hTotal = Number(h.amount);
                  const hRate  = hPmt && hBase ? Math.round(hBase / hPmt * 10000) / 100 : null;
                  const hMult  = hBase > 0 ? Math.round(hTotal / hBase * 100) / 100 : 1;
                  return (
                    <Box key={h.id} sx={{
                      py: 1, px: 1, borderRadius: 1,
                      bgcolor: '#0d0d1a',
                      border: '1px solid #1a1a3e',
                      mb: i < history.length - 1 ? 0.75 : 0,
                    }}>
                      {/* Package name */}
                      <Typography sx={{ color: '#4fc3f7', fontSize: 11, fontWeight: 600, mb: 0.4 }}>
                        📦 {h.package_name || '(пакет не вказано)'}
                      </Typography>

                      {/* Date + what referral paid */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography sx={{ color: '#666', fontSize: 10 }}>
                          🗓 {hDate ? new Date(hDate).toLocaleDateString('uk-UA', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                        </Typography>
                        {hPmt ? (
                          <Typography sx={{ color: '#888', fontSize: 10 }}>
                            · реферал сплатив: <Typography component="span" sx={{ color: '#81c784', fontWeight: 600 }}>€{hPmt.toFixed(2)}</Typography>
                          </Typography>
                        ) : (
                          <Typography sx={{ color: '#555', fontSize: 10 }}>· сума платежу невідома</Typography>
                        )}
                      </Box>

                      {/* Payout to parent */}
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography sx={{ color: '#888', fontSize: 10 }}>💰 Нараховано:</Typography>
                        <Typography sx={{ color: hBonus > 0 ? '#ffd54f' : '#ffb74d', fontSize: 13, fontWeight: 700 }}>
                          €{hTotal.toFixed(2)}
                        </Typography>
                        <Typography sx={{ color: '#555', fontSize: 9 }}>
                          {hPmt && hRate
                            ? hBonus > 0
                              ? `(${hRate}% від €${hPmt.toFixed(2)} = €${hBase.toFixed(2)} × ${hMult}🏆)`
                              : `(${hRate}% від €${hPmt.toFixed(2)})`
                            : `(${hBase.toFixed(2)} базово${hBonus > 0 ? ` + €${hBonus.toFixed(2)} бонус` : ''})`
                          }
                        </Typography>
                      </Box>

                      {/* Contest name if applicable */}
                      {h.contest_title && (
                        <Typography sx={{ color: '#ffd54f', fontSize: 9, mt: 0.4, pl: 0.5 }}>
                          🏆 Акція: «{h.contest_title}» — множник ×{hMult} діє назавжди
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        )}

        <Button size="small" onClick={closeDetail} sx={{ color: '#555', fontSize: 10, mt: 1.5, p: 0, display: 'block' }}>
          Закрити
        </Button>
      </Popover>

      {/* Children (lazy-loaded) */}
      {expanded && children && children.map(child => (
        <TreeNode
          key={child.referred_id}
          node={child}
          depth={depth + 1}
          onDrillDown={onDrillDown}
          payoutMap={payoutMap}
          levelSettings={levelSettings}
          viewedUserId={viewedUserId}
        />
      ))}
    </Box>
  );
}

// ─── Notes editor ─────────────────────────────────────────────────────────────
function NotesEditor({ userId }) {
  const [note, setNote] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get(`/users/${userId}/notes`).then(r => {
      setNote(r.data.note || '');
      setTags(r.data.tags || []);
    }).catch(() => {});
  }, [userId]);

  const save = async () => {
    setSaving(true);
    await API.put(`/users/${userId}/notes`, { note, tags }).catch(() => {});
    setSaving(false);
  };
  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) setTags(ts => [...ts, t]);
    setTagInput('');
  };

  return (
    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#12121f', borderRadius: 1, border: '1px solid #2d2d4e' }}>
      <Typography sx={{ color: '#aaa', fontSize: 12, mb: 1 }}>✏️ Нотатки / Теги</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        {tags.map(t => (
          <Chip key={t} label={t} size="small" onDelete={() => setTags(ts => ts.filter(x => x !== t))}
            sx={{ bgcolor: '#2d3748', color: '#e2e8f0', fontSize: 11 }} />
        ))}
        <TextField size="small" placeholder="Додати тег..." value={tagInput}
          onChange={e => setTagInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addTag()}
          sx={{
            width: 120,
            '& .MuiInputBase-root': { height: 24, fontSize: 11 },
            '& input': { color: '#fff', p: '2px 6px' },
            '& fieldset': { borderColor: '#555' },
          }} />
      </Box>
      <TextField multiline minRows={2} maxRows={4} fullWidth value={note}
        onChange={e => setNote(e.target.value)}
        placeholder="Нотатки про юзера..."
        sx={{
          '& textarea': { color: '#fff', fontSize: 12 },
          '& fieldset': { borderColor: '#555' },
          '& .MuiOutlinedInput-root': { bgcolor: '#0d0d1a' },
        }} />
      <Button size="small" startIcon={<SaveIcon />} onClick={save} disabled={saving}
        sx={{ mt: 1, color: '#90caf9', fontSize: 11 }}>
        {saving ? 'Збереження...' : 'Зберегти'}
      </Button>
    </Box>
  );
}

// ─── Analyse Modal ────────────────────────────────────────────────────────────
function AnalyseModal({ open, onClose, userId, username }) {
  const [model, setModel] = useState('claude-haiku-4-5-20251001');
  const [focus, setFocus] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) { setResult(null); setError(''); }
  }, [open]);

  const run = async () => {
    setLoading(true); setResult(null); setError('');
    try {
      const r = await API.post(`/users/${userId}/analyse`, { model, focus });
      setResult(r.data.result);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Помилка виклику API');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { bgcolor: '#1a1a2e', color: '#fff', border: '1px solid #333' } }}>
      <DialogTitle sx={{ fontSize: 15, pb: 1 }}>
        🤖 AI Аналіз — @{username}
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 8, top: 8, color: '#aaa' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ color: '#aaa', fontSize: 12, mb: 1.5 }}>Модель:</Typography>
        <RadioGroup value={model} onChange={e => setModel(e.target.value)} row sx={{ mb: 2 }}>
          {[
            { id: 'claude-haiku-4-5-20251001', label: 'Haiku', sub: 'швидко / дешево' },
            { id: 'claude-sonnet-4-6', label: 'Sonnet', sub: 'збалансовано' },
            { id: 'claude-opus-4-7', label: 'Opus', sub: 'глибокий аналіз' },
          ].map(m => (
            <FormControlLabel key={m.id} value={m.id}
              control={<Radio size="small" sx={{ color: '#555', '&.Mui-checked': { color: '#90caf9' } }} />}
              label={
                <Box>
                  <Typography sx={{ fontSize: 13, color: '#fff' }}>{m.label}</Typography>
                  <Typography sx={{ fontSize: 10, color: '#888' }}>{m.sub}</Typography>
                </Box>
              }
            />
          ))}
        </RadioGroup>
        <TextField fullWidth size="small" label="Фокус аналізу (опційно)" value={focus}
          onChange={e => setFocus(e.target.value)}
          placeholder='наприклад: "ризик відтоку" або "fraud перевірка"'
          sx={{ mb: 2, '& input': { color: '#fff' }, '& label': { color: '#888' }, '& fieldset': { borderColor: '#555' } }} />
        {loading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {result && (
          <Box sx={{ p: 1.5, bgcolor: '#0d1117', borderRadius: 1, border: '1px solid #30363d' }}>
            <Typography sx={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {result}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ color: '#aaa' }}>Закрити</Button>
        <Button onClick={run} disabled={loading} variant="contained"
          sx={{ bgcolor: '#1e40af', '&:hover': { bgcolor: '#1d4ed8' } }}>
          {loading ? 'Аналізую...' : '▶ Запустити'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Referral Drawer ──────────────────────────────────────────────────────────
function ReferralDrawer({ open, onClose, rootUserId, rootUsername }) {
  const [navStack, setNavStack] = useState([]);
  const [data, setData] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [analyseOpen, setAnalyseOpen] = useState(false);
  const [showAnalyses, setShowAnalyses] = useState(false);
  const [expandedAnalysis, setExpandedAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payoutMap, setPayoutMap] = useState({});
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const currentUser = navStack.length > 0
    ? navStack[navStack.length - 1]
    : { id: rootUserId, username: rootUsername };

  const loadData = useCallback((userId) => {
    setLoading(true); setData(null); setPayoutMap({});
    Promise.all([
      API.get(`/referral-earnings/${userId}`),
      API.get(`/users/${userId}/analyses`),
      API.get(`/referral-earnings/${userId}/payout-log`),
    ]).then(([d, a, pl]) => {
      setData(d.data);
      setAnalyses(a.data);
      setPayoutMap(pl.data || {});
    }).catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (open && rootUserId) {
      setNavStack([]);
      loadData(rootUserId);
    }
  }, [open, rootUserId, loadData]);

  const drillDown = (userId, username) => {
    setNavStack(s => [...s, { id: userId, username }]);
    loadData(userId);
  };
  const goBack = () => {
    const newStack = navStack.slice(0, -1);
    setNavStack(newStack);
    const prev = newStack.length > 0
      ? newStack[newStack.length - 1]
      : { id: rootUserId, username: rootUsername };
    loadData(prev.id);
  };
  const jumpTo = (idx) => {
    if (idx < 0) { setNavStack([]); loadData(rootUserId); return; }
    const newStack = navStack.slice(0, idx + 1);
    setNavStack(newStack);
    loadData(newStack[newStack.length - 1].id);
  };

  const onAnalysisDone = () => {
    API.get(`/users/${currentUser.id}/analyses`).then(r => setAnalyses(r.data)).catch(() => {});
  };

  const deleteAnalysis = async (analysisId) => {
    await API.delete(`/users/${currentUser.id}/analyses/${analysisId}`).catch(() => {});
    setAnalyses(prev => prev.filter(a => a.id !== analysisId));
    if (expandedAnalysis === analysisId) setExpandedAnalysis(null);
  };

  // Root-level nodes: minimum level in response (system uses global depth, not relative)
  const referrals = data?.referrals || [];
  const minLevel = referrals.length > 0 ? Math.min(...referrals.map(r => r.level)) : 1;
  const rootRefs = referrals.filter(r => r.level === minLevel);

  const breadcrumbItems = [
    { label: rootUsername, idx: -1 },
    ...navStack.map((n, i) => ({ label: n.username, idx: i })),
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 740 }, bgcolor: '#0d0d1a', color: '#fff', overflowX: 'hidden', display: 'flex', flexDirection: 'column' } }}>

      {/* Header with breadcrumbs */}
      <Box sx={{ p: 2, borderBottom: '1px solid #2d2d4e', display: 'flex', alignItems: 'flex-start', gap: 1, flexShrink: 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: '#555' }} />}
            sx={{ '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap', overflowX: 'auto' } }}>
            {breadcrumbItems.map((item, i) => {
              const isLast = i === breadcrumbItems.length - 1;
              return isLast ? (
                <Typography key={i} sx={{ fontSize: 13, color: '#90caf9', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  @{item.label}
                </Typography>
              ) : (
                <Link key={i} component="button" onClick={() => jumpTo(item.idx)}
                  sx={{ fontSize: 13, color: '#aaa', cursor: 'pointer', textDecoration: 'none', whiteSpace: 'nowrap', '&:hover': { color: '#fff' } }}>
                  @{item.label}
                </Link>
              );
            })}
          </Breadcrumbs>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {navStack.length > 0 && (
            <Button size="small" onClick={goBack} sx={{ color: '#aaa', fontSize: 11 }}>← Назад</Button>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: '#aaa' }}><CloseIcon /></IconButton>
        </Box>
      </Box>

      {loading && <Box sx={{ textAlign: 'center', pt: 6 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Box sx={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column' }}>

          {/* Summary */}
          <Box sx={{ p: 2, flexShrink: 0 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
              {[
                { label: 'Всього', value: fmtEur(data.user.total_earned), color: '#81c784' },
                { label: 'В проекті', value: fmtEur(data.user.in_project), color: '#4fc3f7' },
                { label: data.user.frozen_next_unlock ? ('🔵 Розморозиться через ' + fmtCountdown(data.user.frozen_next_unlock, now)) : '🔵 Заморожено', value: fmtEur(data.user.frozen_balance), color: '#82b1ff' },
                ...(Number(data.user.suspended_balance) > 0 ? [{ label: '⏸ Заморожено', value: fmtEur(data.user.suspended_balance), color: '#ef9a9a' }] : []),
                { label: 'Виведено', value: fmtEur(data.user.withdrawn), color: '#ffb74d' },
                { label: 'Рефералів', value: data.total_referrals, color: '#ce93d8' },
              ].map(c => (
                <Box key={c.label} sx={{ bgcolor: '#12121f', border: '1px solid #2d2d4e', borderRadius: 1, px: 2, py: 0.75, minWidth: 88, textAlign: 'center' }}>
                  <Typography sx={{ color: c.color, fontSize: 15, fontWeight: 700 }}>{c.value}</Typography>
                  <Typography sx={{ color: '#777', fontSize: 10 }}>{c.label}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              {data.user.is_founder && <Chip label="Founder" size="small" sx={{ bgcolor: '#4527a0', color: '#e8eaf6' }} />}
              {data.user.retention_level && data.user.retention_level !== 'starter' &&
                <Chip label={data.user.retention_level} size="small" sx={{ bgcolor: '#1a237e', color: '#e8eaf6' }} />}
              <Chip label={data.user.status} size="small"
                sx={{ bgcolor: data.user.status === 'active' ? '#1b5e20' : '#4e342e', color: '#fff' }} />
            </Box>

            <NotesEditor userId={currentUser.id} />

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
              <Button size="small" startIcon={<SmartToyIcon />} variant="outlined"
                onClick={() => setAnalyseOpen(true)}
                sx={{ borderColor: '#1e40af', color: '#90caf9', fontSize: 12 }}>
                🤖 AI Аналіз
              </Button>
              <Button size="small" onClick={() => setShowAnalyses(s => !s)}
                sx={{ color: '#888', fontSize: 12, border: '1px solid #333' }}>
                📋 Аналізи ({analyses.length}) {showAnalyses ? '▲' : '▼'}
              </Button>
            </Box>

            {/* Analyses collapse */}
            <Collapse in={showAnalyses}>
              <Box sx={{ mt: 1.5 }}>
                {analyses.length === 0 ? (
                  <Typography sx={{ color: '#555', fontSize: 12, py: 1 }}>
                    Аналізів ще немає. Натисни "🤖 AI Аналіз".
                  </Typography>
                ) : analyses.map(a => (
                  <Box key={a.id} sx={{ mb: 1.5, p: 1.5, bgcolor: '#12121f', borderRadius: 1, border: '1px solid #2d2d4e' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: '#90caf9' }}>{a.model}</Typography>
                        <Typography sx={{ fontSize: 11, color: '#555' }}>{fmtDate(a.created_at)}</Typography>
                      </Box>
                      <Tooltip title="Видалити аналіз">
                        <IconButton size="small" onClick={() => deleteAnalysis(a.id)}
                          sx={{ color: '#555', p: 0.25, '&:hover': { color: '#ef5350' } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {a.focus_prompt && (
                      <Typography sx={{ fontSize: 11, color: '#888', mb: 0.5 }}>Фокус: {a.focus_prompt}</Typography>
                    )}
                    <Collapse in={expandedAnalysis === a.id} collapsedSize={60}>
                      <Typography sx={{ fontSize: 13, color: '#e2e8f0', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                        {a.result}
                      </Typography>
                    </Collapse>
                    <Button size="small"
                      onClick={() => setExpandedAnalysis(expandedAnalysis === a.id ? null : a.id)}
                      sx={{ color: '#555', fontSize: 11, p: 0, mt: 0.5 }}>
                      {expandedAnalysis === a.id ? 'Згорнути' : 'Розгорнути'}
                    </Button>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>

          <Divider sx={{ borderColor: '#2d2d4e', flexShrink: 0 }} />

          {/* Column hint */}
          <Box sx={{ px: 2, py: 0.75, bgcolor: '#080810', borderBottom: '1px solid #1a1a2e', flexShrink: 0, display: 'flex', gap: 1 }}>
            <Box sx={{ width: 40 }} />
            {Object.entries(LEVEL_COLORS).map(([lvl, color]) => (
              <Box key={lvl} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color }} />
                <Typography sx={{ color: '#444', fontSize: 9 }}>L{lvl}</Typography>
              </Box>
            ))}
            <Typography sx={{ color: '#333', fontSize: 9, ml: 'auto' }}>
              Клік на нік → його дерево · ▶ розгорнути рефералів
            </Typography>
          </Box>

          {/* Tree */}
          <Box sx={{ flex: 1 }}>
            {rootRefs.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: '#555' }}>
                <Typography>Рефералів немає</Typography>
              </Box>
            ) : rootRefs.map(r => (
              <TreeNode
                key={r.referred_id}
                node={r}
                depth={0}
                onDrillDown={drillDown}
                payoutMap={payoutMap}
                levelSettings={data?.level_settings || {}}
                viewedUserId={currentUser.id}
              />
            ))}
          </Box>
        </Box>
      )}

      <AnalyseModal
        open={analyseOpen}
        onClose={() => { setAnalyseOpen(false); onAnalysisDone(); }}
        userId={currentUser.id}
        username={currentUser.username}
      />
    </Drawer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ReferralEarningsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(30);
  const [sort, setSort] = useState('total_earned');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, userId: null, username: '' });
  const [analyseModal, setAnalyseModal] = useState({ open: false, userId: null, username: '' });
  const [snack, setSnack] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    API.get('/referral-earnings', { params: { page, per_page: perPage, sort, search } })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => setSnack('Помилка завантаження даних'))
      .finally(() => setLoading(false));
  }, [page, perPage, sort, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = () => { setPage(1); setSearch(searchInput); };
  const openDrawer = (u) => setDrawer({ open: true, userId: u.id, username: u.telegram_username || String(u.telegram_id) });

  return (
    <Box sx={{ p: 3, color: '#fff', minHeight: '100vh', bgcolor: '#0a0a14' }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#90caf9' }}>
          💰 Реферальні доходи
        </Typography>
        <Typography sx={{ color: '#555', fontSize: 13 }}>Юзерів з доходом: {total}</Typography>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder="Пошук по нікнейму..." value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#555', fontSize: 18 }} /></InputAdornment>,
            sx: { color: '#fff', '& fieldset': { borderColor: '#333' }, fontSize: 13 },
          }}
          sx={{ width: 260 }} />
        <Button size="small" onClick={handleSearch} variant="outlined"
          sx={{ borderColor: '#333', color: '#aaa', height: 40 }}>Знайти</Button>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel sx={{ color: '#888' }}>Сортування</InputLabel>
          <Select value={sort} onChange={e => { setSort(e.target.value); setPage(1); }} label="Сортування"
            sx={{ color: '#fff', '& fieldset': { borderColor: '#333' }, fontSize: 13 }}>
            <MenuItem value="total_earned">Всього зароблено ↓</MenuItem>
            <MenuItem value="in_project">В проекті ↓</MenuItem>
            <MenuItem value="l1_count">Рефералів L1 ↓</MenuItem>
            <MenuItem value="last_activity">Остання активність ↓</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ bgcolor: '#12121f', border: '1px solid #2d2d4e', borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ '& th': { color: '#888', fontSize: 12, borderBottom: '1px solid #2d2d4e', py: 1.5 } }}>
              <TableCell>#</TableCell>
              <TableCell>Юзер</TableCell>
              <TableCell>Рівні</TableCell>
              <TableCell align="right">Всього</TableCell>
              <TableCell align="right">В проекті</TableCell>
              <TableCell align="right">Виведено</TableCell>
              <TableCell align="center">Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, borderBottom: 'none' }}>
                  <CircularProgress size={28} />
                </TableCell>
              </TableRow>
            )}
            {!loading && items.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#555', borderBottom: 'none' }}>
                  Юзерів з реферальним доходом не знайдено
                </TableCell>
              </TableRow>
            )}
            {!loading && items.map((u, idx) => (
              <TableRow key={u.id} hover sx={{
                '& td': { borderBottom: '1px solid #1a1a2e', py: 1, fontSize: 13 },
                '&:hover': { bgcolor: '#1a1a2e' },
              }}>
                <TableCell sx={{ color: '#555' }}>{(page - 1) * perPage + idx + 1}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      onClick={() => openDrawer(u)}
                      sx={{ color: '#90caf9', fontWeight: 600, fontSize: 13, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                      @{u.telegram_username || u.telegram_id}
                    </Typography>
                    {u.is_founder && <Chip label="F" size="small" sx={{ bgcolor: '#4527a0', color: '#e8eaf6', fontSize: 9, height: 18 }} />}
                    {u.note && (
                      <Tooltip title={u.note}>
                        <Typography sx={{ fontSize: 11, color: '#888', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          📝 {u.note}
                        </Typography>
                      </Tooltip>
                    )}
                    {(u.tags || []).map(t => (
                      <Chip key={t} label={t} size="small" sx={{ bgcolor: '#2d3748', color: '#e2e8f0', fontSize: 9, height: 18 }} />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[1, 2, 3, 4, 5].map(lvl => {
                      const cnt = u[`l${lvl}_count`];
                      if (!cnt) return null;
                      return (
                        <Chip key={lvl} label={`L${lvl}:${cnt}`} size="small" sx={{
                          bgcolor: LEVEL_COLORS[lvl] + '22', color: LEVEL_COLORS[lvl],
                          borderColor: LEVEL_COLORS[lvl] + '55', border: '1px solid', fontSize: 10, height: 20,
                        }} />
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: '#81c784', fontWeight: 600 }}>{fmtEur(u.total_earned)}</TableCell>
                <TableCell align="right">
                  <Typography sx={{ color: '#4fc3f7', fontSize: 13 }}>{fmtEur(u.in_project)}</Typography>
                  {u.frozen_balance > 0 && (
                    <Typography sx={{ color: '#82b1ff', fontSize: 11 }}>🔵 {fmtEur(u.frozen_balance)}</Typography>
                  )}
                  {Number(u.suspended_balance) > 0 && (
                    <Tooltip title="Заморожено (реферер неактивний)">
                      <Typography sx={{ color: '#ef9a9a', fontSize: 10, cursor: 'default' }}>⏸{fmtEur(u.suspended_balance)}</Typography>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ color: '#ffb74d' }}>{fmtEur(u.withdrawn)}</TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                    <Tooltip title="Деталі">
                      <IconButton size="small" onClick={() => openDrawer(u)} sx={{ color: '#90caf9' }}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="AI Аналіз">
                      <IconButton size="small"
                        onClick={() => setAnalyseModal({ open: true, userId: u.id, username: u.telegram_username || String(u.telegram_id) })}
                        sx={{ color: '#ce93d8' }}>
                        <SmartToyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {total > perPage && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination count={Math.ceil(total / perPage)} page={page} onChange={(_, v) => setPage(v)}
            sx={{ '& .MuiPaginationItem-root': { color: '#aaa' }, '& .Mui-selected': { bgcolor: '#1e40af !important', color: '#fff' } }} />
        </Box>
      )}

      <ReferralDrawer
        open={drawer.open}
        onClose={() => setDrawer(d => ({ ...d, open: false }))}
        rootUserId={drawer.userId}
        rootUsername={drawer.username}
      />

      <AnalyseModal
        open={analyseModal.open}
        onClose={() => setAnalyseModal(m => ({ ...m, open: false }))}
        userId={analyseModal.userId}
        username={analyseModal.username}
      />

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="error" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
